import type { FAQPage, WithContext } from 'schema-dts';
import type { StrapiFaqEntry } from '#variant/apollo/types/types';
import useMarkdownIt from '#variant/composables/useMarkdownIt';

type FaqInput = Pick<StrapiFaqEntry, 'title' | 'content'>;

// Google accepts HTML in acceptedAnswer.text, but plain text is safer. The FAQ
// content is authored in markdown, so render it and reduce to plain text.
function stripMarkdown(content: string): string {
  if (!content) {
    return '';
  }

  const md = useMarkdownIt();

  return md
    .render(content)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
}

export function useGoogleFaqPage(
  faqs: Array<FaqInput> | undefined,
): WithContext<FAQPage> | undefined {
  if (faqs == null || faqs.length === 0) {
    return undefined;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripMarkdown(faq.content),
      },
    })),
  } as WithContext<FAQPage>;
}
